/**
 * The Automations list on a phone and an iPad, in the Season design: a switch, a name and one line per automation. A
 * phone shows rows and keeps the numbers behind a tap; an iPad upright shows cards in two columns; on its side the list
 * sits beside the automation that is open, like Mail. It reads what the computer's Automations reads.
 */
import { type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { AutomationSummary } from "../../shared/types";
import { openedShare } from "../../shared/automation-words";
import { FirstRoomEmpty } from "../components/FirstRoomEmpty";
import { SeasonCard } from "../components/season/Card";
import { Empty } from "../components/season/Empty";
import type { SeasonLayout } from "../components/season/layout";
import { Metrics } from "../components/season/Numbers";
import { Page } from "../components/season/Page";
import { PageHead } from "../components/season/PageHead";
import { Pane, Panes } from "../components/season/Panes";
import { PushedNav } from "../components/season/PushedNav";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { useSendingStatus } from "../sending/SendingContext";
import { heldReason } from "../sending/sendingProtection";
import { SeasonAutomation } from "./SeasonAutomation";
import { NewSheet, ShowSheet } from "./seasonSheets";
import type { Start } from "./starts";
import { createFromStart, useAutomations } from "./useAutomations";
import { errorText, firstRoomOnly, fmt, people, plainLine, roomsLine, waitingRoom } from "./words";
import "../styles/season-autos.css";

const pillsOf = (a: AutomationSummary) => {
  const waiting = waitingRoom(a.status, a.rooms);
  return a.attention || waiting ? (
    <>
      {a.attention ? (
        <Pill tone="warn" dot className="au-pill">
          {a.attention}
        </Pill>
      ) : null}
      {waiting ? (
        <Pill tone="warn" className="au-pill">
          Starts when published
        </Pill>
      ) : null}
    </>
  ) : null;
};

function Row({ a, selected, onSwitch }: { a: AutomationSummary; selected: boolean; onSwitch: (on: boolean) => void }) {
  const on = a.status === "on";
  return (
    <li className={selected ? "k-lr au-r k-on" : "k-lr au-r"}>
      <Link className="k-lr-b" to={`/automations/${a.id}`} aria-label={a.name}>
        <span className={a.attention ? "k-ico brass" : on ? "k-ico wine" : "k-ico"}>
          <Icon name={a.attention ? "info" : "zap"} />
        </span>
        <span className="k-lr-t">
          <b>{a.name}</b>
          <span className="k-clamp">{plainLine(a)}</span>
          {pillsOf(a)}
        </span>
      </Link>
      {a.status === "draft" ? <Pill tone="warn" className="au-draft">Draft</Pill> : <Toggle on={on} onChange={onSwitch} label={`${on ? "Turn off" : "Turn on"} ${a.name}`} />}
    </li>
  );
}

function numbersOf(a: AutomationSummary) {
  const e = a.enroll;
  if (a.status === "draft" || !e) return null;
  if (e.handed && !a.graph.nodes.some((n) => n.type === "email")) return <Metrics items={[{ value: fmt(e.handed), label: "handed off" }]} />;
  const opened = openedShare(e);
  const items = [
    { value: fmt(e.entered), label: "joined" },
    ...(opened === null ? [] : [{ value: `${opened}%`, label: "opened" }]),
    { value: fmt(e.signed), label: "signed up" },
  ];
  return <Metrics three={items.length === 3} items={items} />;
}

function Card({ a, onSwitch }: { a: AutomationSummary; onSwitch: (on: boolean) => void }) {
  const on = a.status === "on";
  return (
    <article className={on || a.status === "draft" ? "k-card au-c" : "k-card au-c au-offc"}>
      <Link className="au-cb" to={`/automations/${a.id}`} aria-label={a.name}>
        <b className="au-ct">{a.name}</b>
        <span className="au-cl">{plainLine(a)}</span>
        {pillsOf(a)}
        {numbersOf(a)}
        <span className="k-rm au-rooms">{roomsLine(a.rooms)}</span>
      </Link>
      {a.status === "draft" ? <Pill tone="warn" className="au-cd">Draft</Pill> : <Toggle className="au-csw" on={on} onChange={onSwitch} label={`${on ? "Turn off" : "Turn on"} ${a.name}`} />}
    </article>
  );
}

export function SeasonAutomations({ layout, openId }: { layout: SeasonLayout; openId?: string | undefined }) {
  const navigate = useNavigate();
  const toast = useToast();
  const sending = useSendingStatus();
  const { data, failed, load, switchOn } = useAutomations();
  const [filter, setFilter] = useState("all");
  const [showing, setShowing] = useState(false);
  const [making, setMaking] = useState(false);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const make = async (start: Start) => {
    setBusy(true);
    try {
      const made = await createFromStart(start);
      setMaking(false);
      navigate(`/automations/${made.id}`);
    } catch (err) {
      toast(errorText(err, "The automation was not made"));
    } finally {
      setBusy(false);
    }
  };
  const side = layout === "side";
  const phone = layout === "phone";

  if (failed && !data) {
    return (
      <Page layout={layout} narrow className="au">
        <PageHead title="Automations" />
        <Empty title="Your automations did not load">
          <Button icon="refresh" onClick={load}>
            Try again
          </Button>
        </Empty>
      </Page>
    );
  }
  if (!data) {
    return (
      <Page layout={layout} narrow className="au">
        <span aria-busy="true" />
        <PageHead title="Automations" />
      </Page>
    );
  }
  const fresh = firstRoomOnly(data);
  if (!data.automations.length || fresh) {
    return (
      <Page layout={layout} narrow className="au">
        <PageHead title="Automations" />
        <SeasonCard>
          <FirstRoomEmpty place="automations" />
        </SeasonCard>
      </Page>
    );
  }
  const list = data.automations;
  const shown = filter === "all" ? list : list.filter((a) => a.rooms.some((r) => r.webinar_id === filter));
  const roomTitle = list.flatMap((a) => a.rooms).find((r) => r.webinar_id === filter)?.title;
  const open = openId ?? (shown.find((a) => a.attention) ?? shown[0])?.id;
  const held = heldReason(sending);
  const onSwitch = (a: AutomationSummary) => (on: boolean) => void switchOn(a, on).then(() => setTick((t) => t + 1));

  const sub = (
    <>
      <b>{people(data.people_now)}</b> in them right now
    </>
  );
  const strip =
    held && held !== "paused" ? (
      <div className="au-note">
        <Icon name="mail" />
        <span>
          <b>These emails won’t go out yet.</b>
        </span>
        <Button onClick={() => navigate("/setup/email")}>Set up sending</Button>
      </div>
    ) : null;
  const filterButton = (
    <button type="button" className="au-filter" onClick={() => setShowing(true)}>
      <span className="au-ft">
        <em>Show</em>
        <b>{filter === "all" ? "All" : (roomTitle ?? "All")}</b>
      </span>
      <span className="au-fn">{shown.length}</span>
      <Icon name="chev" />
    </button>
  );
  const sheets: ReactNode = (
    <>
      <ShowSheet open={showing} onClose={() => setShowing(false)} list={list} value={filter} onPick={setFilter} />
      <NewSheet open={making} busy={busy} onClose={() => setMaking(false)} onContinue={(start) => void make(start)} />
    </>
  );
  const rows = shown.length ? (
    <ul className="k-gl au-gl">
      {shown.map((a) => (
        <Row key={a.id} a={a} selected={side && a.id === open} onSwitch={onSwitch(a)} />
      ))}
    </ul>
  ) : (
    <Empty title="Nothing here" />
  );

  if (side) {
    return (
      <>
        <Panes three className="au-split">
          <Pane kind="list" keep="automations-list" label="Automations">
            <div className="k-sp-lh">
              <div className="k-head au-lh">
                <h1 className="k-title">Automations</h1>
                <Button icon="plus" aria-label="New automation" onClick={() => setMaking(true)}>
                  New
                </Button>
              </div>
            </div>
            <Page layout="side" className="au">
              <p className="k-sub au-lsub">{sub}</p>
              {strip}
              {filterButton}
              {rows}
            </Page>
          </Pane>
          {open ? (
            <SeasonAutomation key={open} id={open} layout="side" embedded tick={tick} onChanged={load} />
          ) : (
            <Pane kind="main" label="The automation that is open">
              <Page layout="side" className="au">
                <Empty title="Nothing here" />
              </Page>
            </Pane>
          )}
        </Panes>
        {sheets}
      </>
    );
  }

  return (
    <Page layout={layout} narrow className="au">
      {phone ? <PushedNav back={{ to: "/more", label: "More" }} /> : null}
      <PageHead
        title="Automations"
        sub={sub}
        {...(phone
          ? {}
          : {
              right: (
                <Button variant="p" icon="plus" onClick={() => setMaking(true)}>
                  New automation
                </Button>
              ),
            })}
      />
      {strip}
      {filterButton}
      {phone ? rows : shown.length ? <div className="k-cols au-cols">{shown.map((a) => <Card key={a.id} a={a} onSwitch={onSwitch(a)} />)}</div> : rows}
      {sheets}
    </Page>
  );
}
